// src/components/employee/EmployeeTaskDashboard.jsx

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Progress,
  List,
  Avatar,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Badge,
  Statistic,
  Space,
  Tooltip,
  Empty,
  Spin
} from 'antd';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Flag,
  MessageCircle,
  Edit2,
  User,
  RefreshCw
} from 'lucide-react';
import moment from 'moment';
import EmployeeTaskService from '../../../services/EmployeeTaskService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EmployeeTaskDashboard = ({ employeeId, currentUser }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();

  useEffect(() => {
    console.log('EmployeeTaskDashboard mounted with employeeId:', employeeId);
    console.log('Current user:', currentUser);
    
    if (employeeId) {
      fetchEmployeeTasks();
    } else {
      console.warn('No employeeId provided to EmployeeTaskDashboard');
      // If no specific employeeId, fetch all tasks
      fetchAllTasks();
    }
  }, [employeeId]);

  const fetchEmployeeTasks = async () => {
    setLoading(true);
    try {
      console.log('Fetching tasks for employee:', employeeId);
      const data = await EmployeeTaskService.getTasksByEmployee(employeeId);
      console.log('Fetched employee tasks:', data);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setTasks(data);
      } else {
        console.error('Expected array of tasks, got:', typeof data);
        setTasks([]);
      }
    } catch (error) {
      console.error('Failed to fetch employee tasks:', error);
      message.error('Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    setLoading(true);
    try {
      console.log('Fetching all tasks');
      const data = await EmployeeTaskService.getAllTasks();
      console.log('Fetched all tasks:', data);
      
      // Filter tasks for current user if available
      let filteredTasks = data;
      if (currentUser && currentUser._id) {
        filteredTasks = data.filter(task => 
          task.assignedTo === currentUser._id || 
          (task.assignedTo && task.assignedTo._id === currentUser._id)
        );
        console.log('Filtered tasks for current user:', filteredTasks);
      }
      
      // Ensure data is an array
      if (Array.isArray(filteredTasks)) {
        setTasks(filteredTasks);
      } else {
        console.error('Expected array of tasks, got:', typeof filteredTasks);
        setTasks([]);
      }
    } catch (error) {
      console.error('Failed to fetch all tasks:', error);
      message.error('Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (employeeId) {
      fetchEmployeeTasks();
    } else {
      fetchAllTasks();
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      console.log('Updating task status:', taskId, newStatus);
      const completedAt = newStatus === 'completed' ? new Date().toISOString() : null;
      await EmployeeTaskService.updateTaskStatus(taskId, newStatus, completedAt);
      message.success('Task status updated');
      handleRefresh();
    } catch (error) {
      console.error('Failed to update task status:', error);
      message.error('Failed to update task status');
    }
  };

  const handleAddComment = async (values) => {
    try {
      console.log('Adding comment to task:', selectedTask._id);
      const userId = currentUser?._id || employeeId || 'unknown';
      await EmployeeTaskService.addComment(selectedTask._id, values.comment, userId);
      message.success('Comment added successfully');
      setCommentModalVisible(false);
      commentForm.resetFields();
      handleRefresh();
    } catch (error) {
      console.error('Failed to add comment:', error);
      message.error('Failed to add comment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      'in-progress': 'blue',
      completed: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red'
    };
    return colors[priority] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4" />,
      'in-progress': <AlertCircle className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      cancelled: <AlertCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed' || status === 'cancelled') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  // Calculate statistics
  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => isOverdue(t.dueDate, t.status)).length,
    completionRate: tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0
  };

  // Get upcoming tasks (sorted by due date)
  const upcomingTasks = tasks
    .filter(task => task.dueDate && task.status !== 'completed' && task.status !== 'cancelled')
    .sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate)))
    .slice(0, 5);

  // Get recent tasks (sorted by creation date)
  const recentTasks = tasks
    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
    .slice(0, 5);

  // Debug logging
  useEffect(() => {
    console.log('Tasks updated:', tasks);
    console.log('Task stats:', taskStats);
    console.log('Upcoming tasks:', upcomingTasks);
    console.log('Recent tasks:', recentTasks);
  }, [tasks]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <Spin size="large" tip="Loading tasks..." />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <Title level={2} className="text-gray-800 mb-0">
            {employeeId ? 'My Tasks Dashboard' : 'Tasks Dashboard'}
          </Title>
          <Button
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
        {!employeeId && !currentUser && (
          <Text type="warning">No employee ID or current user provided. Showing all tasks.</Text>
        )}
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg">
            <Statistic
              title={<span className="text-gray-600">Total Tasks</span>}
              value={taskStats.total}
              valueStyle={{ color: '#3b82f6' }}
              className="text-center"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg">
            <Statistic
              title={<span className="text-gray-600">In Progress</span>}
              value={taskStats.inProgress}
              valueStyle={{ color: '#f59e0b' }}
              className="text-center"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg">
            <Statistic
              title={<span className="text-gray-600">Completed</span>}
              value={taskStats.completed}
              valueStyle={{ color: '#10b981' }}
              className="text-center"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg">
            <Statistic
              title={<span className="text-gray-600">Overdue</span>}
              value={taskStats.overdue}
              valueStyle={{ color: '#ef4444' }}
              className="text-center"
            />
          </Card>
        </Col>
      </Row>

      {/* Progress Overview */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} md={12}>
          <Card title="Task Completion Progress" className="bg-white shadow-sm border-0 rounded-lg">
            <div className="flex flex-col items-center">
              <Progress
                type="circle"
                percent={taskStats.completionRate}
                format={percent => `${percent}%`}
                size={120}
                strokeColor="#10b981"
              />
              <div className="text-center mt-4">
                <Text className="text-gray-600">Overall Completion Rate</Text>
                <div className="text-sm text-gray-500 mt-1">
                  {taskStats.completed} of {taskStats.total} tasks completed
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Task Status Distribution" className="bg-white shadow-sm border-0 rounded-lg">
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    {taskStats.pending}
                  </div>
                  <div className="text-gray-600">Pending</div>
                </div>
              </Col>
              <Col span={12}>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {taskStats.inProgress}
                  </div>
                  <div className="text-gray-600">In Progress</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Task Lists */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span>Upcoming Deadlines ({upcomingTasks.length})</span>
              </div>
            }
            className="bg-white shadow-sm border-0 rounded-lg"
            bodyStyle={{ height: '400px', overflowY: 'auto' }}
          >
            {upcomingTasks.length > 0 ? (
              <List
                dataSource={upcomingTasks}
                renderItem={task => (
                  <List.Item
                    actions={[
                      <Select
                        value={task.status}
                        className="w-28"
                        onChange={(value) => handleStatusChange(task._id, value)}
                        size="small"
                      >
                        <Option value="pending">Pending</Option>
                        <Option value="in-progress">In Progress</Option>
                        <Option value="completed">Completed</Option>
                        <Option value="cancelled">Cancelled</Option>
                      </Select>
                    ]}
                    className="px-2 hover:bg-gray-50 rounded-md"
                  >
                    <List.Item.Meta
                      avatar={
                        <Badge dot={isOverdue(task.dueDate, task.status)} color="red" offset={[-2, 2]}>
                          <Avatar className="bg-blue-100">
                            {getStatusIcon(task.status)}
                          </Avatar>
                        </Badge>
                      }
                      title={
                        <div className="flex items-center space-x-2">
                          <Text strong className="text-gray-900" ellipsis={{ tooltip: task.title }}>
                            {task.title || 'Untitled Task'}
                          </Text>
                          <Tag 
                            color={getPriorityColor(task.priority)} 
                            size="small"
                            className="flex items-center space-x-1"
                          >
                            <Flag className="w-3 h-3" />
                            <span>{task.priority || 'medium'}</span>
                          </Tag>
                        </div>
                      }
                      description={
                        <div className="space-y-1">
                          <div className="text-gray-600 text-sm">
                            {task.description || 'No description'}
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className={`${isOverdue(task.dueDate, task.status) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                              Due: {moment(task.dueDate).format('MMM DD, YYYY')}
                            </span>
                            {isOverdue(task.dueDate, task.status) && (
                              <Tag color="red" size="small">Overdue</Tag>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No upcoming deadlines" className="py-8" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-green-600" />
                <span>Recent Tasks ({recentTasks.length})</span>
              </div>
            }
            className="bg-white shadow-sm border-0 rounded-lg"
            bodyStyle={{ height: '400px', overflowY: 'auto' }}
          >
            {recentTasks.length > 0 ? (
              <List
                dataSource={recentTasks}
                renderItem={task => (
                  <List.Item
                    actions={[
                      <Tooltip title="View Details">
                        <Button
                          type="text"
                          className="p-1 hover:bg-gray-100 rounded-md"
                          onClick={() => {
                            setSelectedTask(task);
                            setDetailModalVisible(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                      </Tooltip>,
                      <Tooltip title="Add Comment">
                        <Button
                          type="text"
                          className="p-1 hover:bg-gray-100 rounded-md"
                          onClick={() => {
                            setSelectedTask(task);
                            setCommentModalVisible(true);
                          }}
                        >
                          <MessageCircle className="w-4 h-4 text-green-600" />
                        </Button>
                      </Tooltip>
                    ]}
                    className="px-2 hover:bg-gray-50 rounded-md"
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar className="bg-green-100">
                          {getStatusIcon(task.status)}
                        </Avatar>
                      }
                      title={
                        <div className="flex items-center space-x-2">
                          <Text strong className="text-gray-900" ellipsis={{ tooltip: task.title }}>
                            {task.title || 'Untitled Task'}
                          </Text>
                          <Tag 
                            color={getStatusColor(task.status)} 
                            size="small"
                            className="flex items-center space-x-1"
                          >
                            {getStatusIcon(task.status)}
                            <span>{task.status || 'pending'}</span>
                          </Tag>
                        </div>
                      }
                      description={
                        <div className="space-y-1">
                          <div className="text-gray-600 text-sm">
                            {task.description || 'No description'}
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Text type="secondary" className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>Created: {moment(task.createdAt).format('MMM DD, YYYY')}</span>
                            </Text>
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No recent tasks" className="py-8" />
            )}
          </Card>
        </Col>
      </Row>

      {/* All Tasks Table */}
      <Row gutter={[16, 16]} className="mt-6">
        <Col span={24}>
          <Card 
            title={`All Tasks (${tasks.length})`} 
            className="bg-white shadow-sm border-0 rounded-lg"
          >
            {tasks.length > 0 ? (
              <List
                dataSource={tasks}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
                }}
                renderItem={task => (
                  <List.Item
                    key={task._id}
                    actions={[
                      <Select
                        value={task.status}
                        className="w-28"
                        onChange={(value) => handleStatusChange(task._id, value)}
                      >
                        <Option value="pending">Pending</Option>
                        <Option value="in-progress">In Progress</Option>
                        <Option value="completed">Completed</Option>
                        <Option value="cancelled">Cancelled</Option>
                      </Select>,
                      <Space>
                        <Button
                          type="text"
                          className="flex items-center space-x-1 px-3 py-1 hover:bg-gray-100 rounded-md"
                          onClick={() => {
                            setSelectedTask(task);
                            setDetailModalVisible(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                          <span>Details</span>
                        </Button>
                        <Button
                          type="text"
                          className="flex items-center space-x-1 px-3 py-1 hover:bg-gray-100 rounded-md"
                          onClick={() => {
                            setSelectedTask(task);
                            setCommentModalVisible(true);
                          }}
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>Comment</span>
                        </Button>
                      </Space>
                    ]}
                    className="px-4 py-3 hover:bg-gray-50 rounded-lg mb-2"
                  >
                    <List.Item.Meta
                      avatar={
                        <Badge dot={isOverdue(task.dueDate, task.status)} color="red" offset={[-2, 2]}>
                          <Avatar className="bg-blue-100">
                            {getStatusIcon(task.status)}
                          </Avatar>
                        </Badge>
                      }
                      title={
                        <div className="flex items-center space-x-3">
                          <Text strong className="text-gray-900">
                            {task.title || 'Untitled Task'}
                          </Text>
                          <Space>
                            <Tag color={getStatusColor(task.status)} className="flex items-center space-x-1">
                              {getStatusIcon(task.status)}
                              <span>{(task.status || 'pending').toUpperCase()}</span>
                            </Tag>
                            <Tag color={getPriorityColor(task.priority)} className="flex items-center space-x-1">
                              <Flag className="w-3 h-3" />
                              <span>{(task.priority || 'medium').toUpperCase()}</span>
                            </Tag>
                          </Space>
                        </div>
                      }
                      description={
                        <div className="space-y-2">
                          <Paragraph ellipsis={{ rows: 2 }} className="text-gray-600 mb-2">
                            {task.description || 'No description provided'}
                          </Paragraph>
                          <Space wrap className="text-sm">
                            <Text type="secondary" className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>Created: {moment(task.createdAt).format('MMM DD, YYYY')}</span>
                            </Text>
                            {task.dueDate && (
                              <Text type={isOverdue(task.dueDate, task.status) ? "danger" : "secondary"} className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {moment(task.dueDate).format('MMM DD, YYYY')}</span>
                              </Text>
                            )}
                            {task.completedAt && (
                              <Text type="success" className="flex items-center space-x-1">
                                <CheckCircle className="w-4 h-4" />
                                <span>Completed: {moment(task.completedAt).format('MMM DD, YYYY')}</span>
                              </Text>
                            )}
                          </Space>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                description={
                  <div className="text-center">
                    <p className="text-gray-500 mb-2">No tasks found</p>
                    <Button type="primary" onClick={handleRefresh}>
                      Refresh Tasks
                    </Button>
                  </div>
                } 
                className="py-12" 
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Task Detail Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <Edit2 className="w-5 h-5" />
            <span>Task Details</span>
          </div>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
        className="rounded-lg"
      >
        {selectedTask && (
          <div className="mt-4">
            <div className="mb-6">
              <Title level={4} className="text-gray-800 mb-2">
                {selectedTask.title || 'Untitled Task'}
              </Title>
              <Paragraph className="text-gray-600">
                {selectedTask.description || 'No description provided'}
              </Paragraph>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="font-semibold text-gray-700">Status:</span>
                <div className="mt-1">
                  <Tag color={getStatusColor(selectedTask.status)} className="flex items-center space-x-1 inline-flex">
                    {getStatusIcon(selectedTask.status)}
                    <span>{(selectedTask.status || 'pending').toUpperCase()}</span>
                  </Tag>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Priority:</span>
                <div className="mt-1">
                  <Tag color={getPriorityColor(selectedTask.priority)} className="flex items-center space-x-1 inline-flex">
                    <Flag className="w-4 h-4" />
                    <span>{(selectedTask.priority || 'medium').toUpperCase()}</span>
                  </Tag>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="font-semibold text-gray-700">Due Date:</span>
                <div className="flex items-center space-x-2 mt-1">
                  {selectedTask.dueDate ? (
                    <Text type={isOverdue(selectedTask.dueDate, selectedTask.status) ? "danger" : "default"} className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{moment(selectedTask.dueDate).format('MMM DD, YYYY')}</span>
                      {isOverdue(selectedTask.dueDate, selectedTask.status) && (
                        <Tag color="red" size="small">Overdue</Tag>
                      )}
                    </Text>
                  ) : (
                    <span className="text-gray-400">No due date</span>
                  )}
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Created:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{moment(selectedTask.createdAt).format('MMM DD, YYYY')}</span>
                </div>
              </div>
            </div>

            {selectedTask.completedAt && (
              <div className="mb-4">
                <span className="font-semibold text-gray-700">Completed:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700">{moment(selectedTask.completedAt).format('MMM DD, YYYY HH:mm')}</span>
                </div>
              </div>
            )}

            {selectedTask.comments && selectedTask.comments.length > 0 && (
              <div className="mt-6">
                <Title level={5} className="text-gray-800 mb-3">Comments ({selectedTask.comments.length}):</Title>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {selectedTask.comments.map((comment, index) => (
                    <Card key={index} size="small" className="bg-gray-50 border-gray-200">
                      <Paragraph className="text-gray-700 mb-2">{comment.comment}</Paragraph>
                      <Text type="secondary" className="text-sm">
                        {moment(comment.createdAt).format('MMM DD, YYYY HH:mm')}
                      </Text>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <Button
                type="primary"
                className="flex items-center space-x-2 mx-auto bg-blue-600 hover:bg-blue-700 border-blue-600"
                onClick={() => {
                  setDetailModalVisible(false);
                  setCommentModalVisible(true);
                }}
              >
                <MessageCircle className="w-4 h-4" />
                <span>Add Comment</span>
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <MessageCircle className="w-5 h-5" />
            <span>Add Comment</span>
          </div>
        }
        visible={commentModalVisible}
        onCancel={() => {
          setCommentModalVisible(false);
          commentForm.resetFields();
        }}
        footer={null}
        width={500}
        className="rounded-lg"
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleAddComment}
          className="mt-4"
        >
          <Form.Item
            name="comment"
            label={<span className="text-gray-700 font-medium">Comment</span>}
            rules={[{ required: true, message: 'Please enter a comment' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter your comment here..."
              className="rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="bg-green-600 hover:bg-green-700 border-green-600"
              block
            >
              Submit Comment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeTaskDashboard;